import { ApiError } from "../services/api";
import { login, logout, resendOtp, verifyOtp } from "../services/auth";
import { getMailPreview } from "../services/mail";
import { fetchUserMatters, UserMatter } from "../services/matter";
import { session } from "../services/session";
import { createTask } from "../services/task";
import { getApiBaseUrl } from "../services/api";
import { ADDIN_DEV_ORIGIN, TASK_TITLE_MAX_LENGTH } from "../config";

declare const Office: any;

type ViewId = "login" | "otp" | "main";

interface Pending2FA {
  email: string;
  verifyToken: string;
  pendingData: Record<string, unknown>;
}

let pending2FA: Pending2FA | null = null;
let matters: UserMatter[] = [];

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function setText(id: string, text: string): void {
  const el = $(id);
  if (el) {
    el.textContent = text;
  }
}

function setInputValue(id: string, value: string): void {
  const el = $(id) as HTMLInputElement | null;
  if (el) {
    el.value = value;
  }
}

function showView(view: ViewId): void {
  const views: Record<ViewId, string> = {
    login: "view-login",
    otp: "view-otp",
    main: "view-main",
  };

  Object.values(views).forEach((viewId) => {
    $(viewId)?.classList.add("procxo-view--hidden");
  });
  $(views[view])?.classList.remove("procxo-view--hidden");
}

function showMessage(text: string, type: "success" | "error" | "info" = "info"): void {
  const banner = $("message-banner");
  if (!banner) {
    return;
  }
  banner.textContent = text;
  banner.classList.remove("procxo-message--hidden", "procxo-message--success", "procxo-message--error", "procxo-message--info");
  banner.classList.add(`procxo-message--${type}`);
}

function hideMessage(): void {
  $("message-banner")?.classList.add("procxo-message--hidden");
}

function setLoading(buttonId: string, loading: boolean, loadingLabel?: string): void {
  const btn = $(buttonId) as HTMLButtonElement | null;
  if (!btn) {
    return;
  }
  btn.disabled = loading;
  if (loading && loadingLabel) {
    btn.dataset.originalLabel = btn.textContent ?? "";
    btn.textContent = loadingLabel;
  } else if (btn.dataset.originalLabel) {
    btn.textContent = btn.dataset.originalLabel;
    delete btn.dataset.originalLabel;
  }
}

function updateSessionStatus(): void {
  if (session.isAuthenticated()) {
    const name = session.getUserName() || session.getUserEmail() || "User";
    setText("session-status", `Signed in as ${name}`);
  } else {
    setText("session-status", "Not signed in");
  }
}

function updateApiInfo(): void {
  setText("api-info", `API: ${getApiBaseUrl()} · Add-in: ${ADDIN_DEV_ORIGIN}`);
}

function loadMailIntoUi(): void {
  const preview = getMailPreview();
  if (!preview) {
    setText("email-subject", "Open an email to continue");
    setText("email-sender", "—");
    setInputValue("task-title", "");
    return;
  }

  setText("email-subject", preview.subject || "No subject");
  setText("email-sender", preview.sender);
  setInputValue("task-title", preview.subject.slice(0, TASK_TITLE_MAX_LENGTH));
}

function populateMatterDropdown(items: UserMatter[]): void {
  const select = $("matter-select") as HTMLSelectElement | null;
  if (!select) {
    return;
  }

  select.innerHTML = "";
  if (items.length === 0) {
    const option = document.createElement("option");
    option.value = "";
    option.textContent = "No assigned projects found";
    select.appendChild(option);
    select.disabled = true;
    return;
  }

  const placeholder = document.createElement("option");
  placeholder.value = "";
  placeholder.textContent = "Select a project…";
  select.appendChild(placeholder);

  items.forEach((matter) => {
    const option = document.createElement("option");
    option.value = String(matter.matter_id);
    option.textContent = matter.project_name;
    select.appendChild(option);
  });

  select.disabled = false;
}

function updateCreateButtonState(): void {
  const titleInput = $("task-title") as HTMLInputElement | null;
  const matterSelect = $("matter-select") as HTMLSelectElement | null;
  const createBtn = $("create-task-btn") as HTMLButtonElement | null;
  if (!titleInput || !matterSelect || !createBtn) {
    return;
  }

  const hasTitle = titleInput.value.trim().length > 0;
  const hasMatter = matterSelect.value !== "";
  createBtn.disabled = !hasTitle || !hasMatter || matterSelect.disabled;
}

async function loadMatters(): Promise<void> {
  const userId = session.getUserId();
  if (userId == null) {
    return;
  }

  const select = $("matter-select") as HTMLSelectElement | null;
  if (select) {
    select.disabled = true;
    select.innerHTML = '<option value="">Loading projects…</option>';
  }

  try {
    matters = await fetchUserMatters(userId);
    populateMatterDropdown(matters);
  } catch (error) {
    populateMatterDropdown([]);
    const message = error instanceof ApiError ? error.message : "Failed to load projects";
    showMessage(message, "error");
  } finally {
    updateCreateButtonState();
  }
}

async function showAuthenticatedView(): Promise<void> {
  updateSessionStatus();
  showView("main");
  hideMessage();
  loadMailIntoUi();
  await loadMatters();
  updateCreateButtonState();
}

async function handleLoginSubmit(event: Event): Promise<void> {
  event.preventDefault();
  hideMessage();

  const email = ( $("login-email") as HTMLInputElement).value.trim();
  const password = ( $("login-password") as HTMLInputElement).value;

  if (!email || !password) {
    showMessage("Email and password are required.", "error");
    return;
  }

  setLoading("login-submit", true, "Signing in…");
  try {
    const result = await login(email, password);
    if (result.requires2FA) {
      if (!result.verifyTwoFactorOtpToken) {
        showMessage("2FA is enabled but no verification token was returned.", "error");
        return;
      }
      pending2FA = {
        email,
        verifyToken: result.verifyTwoFactorOtpToken,
        pendingData: result.pendingData ?? {},
      };
      showView("otp");
      showMessage(result.message ?? "Enter the OTP sent to your email.", "info");
      return;
    }

    await showAuthenticatedView();
    showMessage(result.message ?? "Signed in successfully.", "success");
  } catch (error) {
    const message = error instanceof ApiError ? error.message : "An error occurred during login";
    showMessage(message, "error");
  } finally {
    setLoading("login-submit", false);
  }
}

async function handleOtpSubmit(event: Event): Promise<void> {
  event.preventDefault();
  hideMessage();

  if (!pending2FA) {
    showMessage("Session expired. Please sign in again.", "error");
    showView("login");
    return;
  }

  const otp = ( $("otp-code") as HTMLInputElement).value.trim();
  if (otp.length !== 6) {
    showMessage("Please enter a valid 6-digit OTP.", "error");
    return;
  }

  setLoading("otp-submit", true, "Verifying…");
  try {
    await verifyOtp(pending2FA.email, otp, pending2FA.verifyToken, pending2FA.pendingData);
    pending2FA = null;
    await showAuthenticatedView();
    showMessage("Signed in successfully.", "success");
  } catch (error) {
    const message = error instanceof ApiError ? error.message : "Invalid OTP. Please try again.";
    showMessage(message, "error");
  } finally {
    setLoading("otp-submit", false);
  }
}

async function handleOtpResend(): Promise<void> {
  if (!pending2FA) {
    return;
  }

  hideMessage();
  setLoading("otp-resend", true, "Sending…");
  try {
    await resendOtp(pending2FA.email);
    showMessage("OTP sent. Check your email.", "success");
  } catch (error) {
    const message = error instanceof ApiError ? error.message : "Failed to resend OTP";
    showMessage(message, "error");
  } finally {
    setLoading("otp-resend", false);
  }
}

function handleOtpBack(): void {
  pending2FA = null;
  hideMessage();
  showView("login");
}

async function handleCreateTask(event: Event): Promise<void> {
  event.preventDefault();
  hideMessage();

  const userId = session.getUserId();
  if (userId == null) {
    showMessage("Session expired. Please sign in again.", "error");
    showView("login");
    return;
  }

  const title = ( $("task-title") as HTMLInputElement).value.trim();
  const matterId = Number(( $("matter-select") as HTMLSelectElement).value);

  if (!title) {
    showMessage("Task title is required.", "error");
    return;
  }
  if (!matterId) {
    showMessage("Please select a project.", "error");
    return;
  }
  if (title.length > TASK_TITLE_MAX_LENGTH) {
    showMessage(`Task title must be ${TASK_TITLE_MAX_LENGTH} characters or fewer.`, "error");
    return;
  }

  setLoading("create-task-btn", true, "Creating…");
  try {
    const response = await createTask({
      matterId,
      title,
      assignedUserIds: [userId],
      status: "To-do",
    }) as Record<string, unknown>;

    const message =
      typeof response.message === "string" ? response.message : "Task created successfully";
    showMessage(message, "success");

    const matterSelect = $("matter-select") as HTMLSelectElement;
    matterSelect.value = "";
    updateCreateButtonState();
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 401) {
      logout();
      showView("login");
      showMessage("Session expired. Please sign in again.", "error");
      updateSessionStatus();
      return;
    }
    const message = error instanceof ApiError ? error.message : "Failed to create task";
    showMessage(message, "error");
  } finally {
    setLoading("create-task-btn", false);
  }
}

function handleSignOut(): void {
  logout();
  pending2FA = null;
  hideMessage();
  updateSessionStatus();
  showView("login");
}

function bindEvents(): void {
  $("login-form")?.addEventListener("submit", (e) => void handleLoginSubmit(e));
  $("otp-form")?.addEventListener("submit", (e) => void handleOtpSubmit(e));
  $("otp-resend")?.addEventListener("click", () => void handleOtpResend());
  $("otp-back")?.addEventListener("click", handleOtpBack);
  $("task-form")?.addEventListener("submit", (e) => void handleCreateTask(e));
  $("sign-out-btn")?.addEventListener("click", handleSignOut);
  $("task-title")?.addEventListener("input", updateCreateButtonState);
  $("matter-select")?.addEventListener("change", updateCreateButtonState);
}

Office.onReady(async (info) => {
  if (info.host !== Office.HostType.Outlook) {
    return;
  }

  await session.hydrateFromOfficeStorage();
  bindEvents();
  updateApiInfo();

  if (session.isAuthenticated()) {
    await showAuthenticatedView();
  } else {
    updateSessionStatus();
    showView("login");
  }
});
